import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MarketingWhyChooseComponent } from '../../components/marketing-why-choose.component';
import { MarketingSuccessRoadmapComponent } from '../../components/marketing-success-roadmap.component';
import { buildPageTitle } from '../../../shared/constants/site-meta';

interface Testimonial {
  name: string;
  course: string;
  avatar: string;
  rating: number;
  review: string;
}

interface Milestone {
  pill: string;
  title: string;
  description: string;
  image: string;
  ctaLabel: string;
  ctaHref: string;
}

/**
 * Static, pixel-perfect premium homepage at `/home1`.
 *
 * This page is intentionally NOT wired to the CMS / dynamic section renderer.
 * It is the visual source of truth for spacing, typography, motion and
 * responsive behaviour. Sections will later be extracted and adapted into the
 * reusable section renderer once the design is approved.
 */
@Component({
  selector: 'app-home1-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MarketingWhyChooseComponent, MarketingSuccessRoadmapComponent],
  templateUrl: './home1-page.component.html',
})
export class Home1PageComponent implements OnInit {
  private readonly title = inject(Title);

  readonly navLinks = [
    { label: 'Home', href: '#' },
    { label: 'Destinations', href: '#destinations' },
    { label: 'Services', href: '#why' },
    { label: 'Scholarships', href: '#cta' },
  ];

  readonly heroFeatures = [
    'Personalized counseling for every student',
    '500+ universities across 15+ countries',
    'End-to-end support from application to visa',
  ];

  readonly stats = [
    { value: '12K+', label: 'Students Counseled' },
    { value: '99%', label: 'Visa Success Rate' },
    { value: '500+', label: 'Partner Universities' },
    { value: '15+', label: 'Study Destinations' },
  ];

  readonly destinations = [
    { name: 'Germany', flag: '🇩🇪', universities: 120, image: 'germany' },
    { name: 'France', flag: '🇫🇷', universities: 95, image: 'france' },
    { name: 'United Kingdom', flag: '🇬🇧', universities: 150, image: 'uk' },
    { name: 'Canada', flag: '🇨🇦', universities: 110, image: 'canada' },
  ];

  readonly miniDestinations = [
    { name: 'United States', flag: '🇺🇸', image: 'usa' },
    { name: 'Australia', flag: '🇦🇺', image: 'australia' },
    { name: 'Ireland', flag: '🇮🇪', image: 'ireland' },
    { name: 'New Zealand', flag: '🇳🇿', image: 'new-zealand' },
  ];

  readonly universities = [
    { name: 'TU Munich', slug: 'tum' },
    { name: 'Sorbonne', slug: 'sorbonne' },
    { name: 'Imperial College', slug: 'imperial' },
    { name: 'University of Toronto', slug: 'uoft' },
    { name: 'MIT', slug: 'mit' },
    { name: 'Trinity College Dublin', slug: 'trinity' },
  ];

  readonly milestones: Milestone[] = [
    {
      pill: 'Featured Milestone',
      title: 'One Scholarship\nat a Time',
      description:
        'Career Grad Global helped a student secure a Government Scholarship, presented by Riniki Bhuyan Sarma — a proud moment celebrating our commitment to authentic guidance, scholarship success, and student excellence.',
      image: 'milestone-1',
      ctaLabel: 'Read the Full Story',
      ctaHref: '#cta',
    },
    {
      pill: 'Featured Milestone',
      title: 'From Tier-3 College\nto Ivy League',
      description:
        'A first-generation student from a small-town college landed a fully-funded Masters at Cornell. We rebuilt her profile from the ground up — research projects, letters of recommendation and an SOP that finally told her real story.',
      image: 'milestone-2',
      ctaLabel: 'Read the Full Story',
      ctaHref: '#cta',
    },
    {
      pill: 'Featured Milestone',
      title: 'IELTS 5.5 to 8.0\nin 90 Days',
      description:
        'After two failed attempts, our test-prep team rebuilt his strategy from scratch. He cleared IELTS with an 8.0, unlocked a £4,000 scholarship at Manchester and started his September intake on time.',
      image: 'milestone-3',
      ctaLabel: 'Read the Full Story',
      ctaHref: '#cta',
    },
    {
      pill: 'Featured Milestone',
      title: 'Visa Approved\nAfter Two Rejections',
      description:
        'Twice rejected for a Canadian study permit, this student almost gave up. Our visa team rewrote her SOP, re-organised her financials and ran ten mock interviews. She is now studying at UBC Vancouver.',
      image: 'milestone-4',
      ctaLabel: 'Read the Full Story',
      ctaHref: '#cta',
    },
  ];

  readonly milestoneSlide = signal(0);

  prevMilestoneSlide(): void {
    this.milestoneSlide.update((s) => Math.max(0, s - 1));
  }

  nextMilestoneSlide(): void {
    this.milestoneSlide.update((s) =>
      Math.min(this.milestones.length - 1, s + 1),
    );
  }

  setMilestoneSlide(i: number): void {
    this.milestoneSlide.set(i);
  }

  readonly testimonials: Testimonial[] = [
    {
      name: 'Aanya Patel',
      course: 'MSc Data Science · TU Munich',
      avatar: 'student-1',
      rating: 5,
      review:
        'Career Grad made my Germany dream real. From SOP drafts to visa interview prep, everything was structured and stress-free.',
    },
    {
      name: 'Rohan Mehta',
      course: 'MBA · ESCP Paris',
      avatar: 'student-2',
      rating: 5,
      review:
        'I had no idea where to start. They mapped a plan in week one and I had three admits by the end of the semester.',
    },
    {
      name: 'Sneha Iyer',
      course: 'MS Computer Science · UofT',
      avatar: 'student-3',
      rating: 5,
      review:
        'The scholarship support alone paid back the counseling fee five times over. My counselor felt like a family member.',
    },
    {
      name: 'Vikram Singh',
      course: 'BSc Computer Science · University of Manchester',
      avatar: 'student-4',
      rating: 5,
      review:
        'My IELTS jumped from 6.0 to 7.5 in six weeks. The mock visa interviews were brutal but I sailed through the real one.',
    },
    {
      name: 'Rahul Sharma',
      course: 'MS Mechanical Engineering · TU Berlin',
      avatar: 'student-5',
      rating: 5,
      review:
        'I had three offer letters in hand within four months. Their Germany-specific guidance is in a different league.',
    },
    {
      name: 'Priya Nair',
      course: 'MS Biotechnology · UBC Vancouver',
      avatar: 'student-6',
      rating: 5,
      review:
        'My counselor caught a missing document the day before the deadline. That attention to detail saved my entire intake.',
    },
    {
      name: 'Aditya Verma',
      course: 'MS Finance · Bocconi Milan',
      avatar: 'student-7',
      rating: 5,
      review:
        'The SOP they crafted for me was so sharp that two universities messaged me directly. Career Grad simply gets it.',
    },
    {
      name: 'Ishita Reddy',
      course: 'MA International Relations · Sciences Po Paris',
      avatar: 'student-8',
      rating: 5,
      review:
        'I never thought Sciences Po was within reach. Their team turned my profile around in a way I could not have imagined.',
    },
    {
      name: 'Karan Malhotra',
      course: 'MS Information Systems · NUS Singapore',
      avatar: 'student-9',
      rating: 5,
      review:
        'They cracked NUS for me with a partial scholarship. From shortlist to landing in Singapore — every step had a plan.',
    },
  ];

  /** Groups of 3 testimonials per slide. */
  readonly testimonialSlides = computed<Testimonial[][]>(() => {
    const groups: Testimonial[][] = [];
    for (let i = 0; i < this.testimonials.length; i += 3) {
      groups.push(this.testimonials.slice(i, i + 3));
    }
    return groups;
  });

  readonly testimonialSlide = signal(0);

  prevTestimonialSlide(): void {
    this.testimonialSlide.update((s) => Math.max(0, s - 1));
  }

  nextTestimonialSlide(): void {
    this.testimonialSlide.update((s) =>
      Math.min(this.testimonialSlides().length - 1, s + 1),
    );
  }

  setTestimonialSlide(i: number): void {
    this.testimonialSlide.set(i);
  }

  readonly footerColumns = [
    {
      heading: 'Career Grad',
      links: [
        { label: 'About', href: '#' },
        { label: 'Careers', href: '#' },
        { label: 'Press', href: '#' },
        { label: 'Contact', href: '#' },
      ],
    },
    {
      heading: 'Destinations',
      links: [
        { label: 'Germany', href: '#' },
        { label: 'United Kingdom', href: '#' },
        { label: 'Canada', href: '#' },
        { label: 'Australia', href: '#' },
      ],
    },
    {
      heading: 'Services',
      links: [
        { label: 'Counseling', href: '#' },
        { label: 'Applications', href: '#' },
        { label: 'Visa Support', href: '#' },
        { label: 'Scholarships', href: '#' },
      ],
    },
    {
      heading: 'Resources',
      links: [
        { label: 'Blog', href: '#' },
        { label: 'University Finder', href: '#' },
        { label: 'Test Prep', href: '#' },
        { label: 'FAQs', href: '#' },
      ],
    },
  ];

  readonly form = {
    fullName: '',
    email: '',
    phone: '',
    destination: '',
  };

  /** Demo submit handler — `/home1` is intentionally not wired to the API. */
  onLeadSubmit(): void {
    // no-op; static benchmark page
  }

  ngOnInit(): void {
    this.title.setTitle(buildPageTitle());
  }
}
